/**
 * The particle update as plain JavaScript over the same typed arrays the GPU kernel reads and
 * writes: the safety valve when a backend refuses the compute kernel, the path `?particles=cpu`
 * forces, and the executable specification the kernel is tested against.
 *
 * Layout per particle (four floats each):
 *   emitA: x, y, z, born      emitB: vx, vy, vz, life     emitC: size, gravity, drag, bounce
 *   emitD: r, g, b, fade      stateA: x, y, z, bornSeen   stateB: vx, vy, vz, age
 */
export interface ParticleBuffers {
  count: number;
  emitA: Float32Array;
  emitB: Float32Array;
  emitC: Float32Array;
  emitD: Float32Array;
  stateA: Float32Array;
  stateB: Float32Array;
}

export function createParticleBuffers(count: number): ParticleBuffers {
  return {
    count,
    emitA: new Float32Array(count * 4),
    emitB: new Float32Array(count * 4),
    emitC: new Float32Array(count * 4),
    emitD: new Float32Array(count * 4),
    stateA: new Float32Array(count * 4),
    stateB: new Float32Array(count * 4),
  };
}

/** The floor debris bounces on; arenas keep their decks at y = 0. */
export const FLOOR_Y = 0.08;
export const BOUNCE_RESTITUTION = 0.35;
export const BOUNCE_FRICTION = 0.6;

/** One step of every particle: spawn when the birth stamp changed, else integrate. */
export function stepParticlesCpu(b: ParticleBuffers, now: number, dt: number): void {
  const { emitA, emitB, emitC, stateA, stateB } = b;
  for (let i = 0; i < b.count; i++) {
    const o = i * 4;
    const born = emitA[o + 3]!;
    if (stateA[o + 3] !== born) {
      stateA[o] = emitA[o]!;
      stateA[o + 1] = emitA[o + 1]!;
      stateA[o + 2] = emitA[o + 2]!;
      stateA[o + 3] = born;
      stateB[o] = emitB[o]!;
      stateB[o + 1] = emitB[o + 1]!;
      stateB[o + 2] = emitB[o + 2]!;
      stateB[o + 3] = 0;
      continue;
    }
    const age = now - born;
    if (age >= emitB[o + 3]!) continue;
    let vx = stateB[o]!;
    let vy = stateB[o + 1]!;
    let vz = stateB[o + 2]!;
    vy += emitC[o + 1]! * dt;
    const damp = Math.max(0, 1 - emitC[o + 2]! * dt);
    vx *= damp;
    vy *= damp;
    vz *= damp;
    const x = stateA[o]! + vx * dt;
    let y = stateA[o + 1]! + vy * dt;
    const z = stateA[o + 2]! + vz * dt;
    if (emitC[o + 3]! > 0.5 && y < FLOOR_Y && vy < 0) {
      y = FLOOR_Y;
      vy = -vy * BOUNCE_RESTITUTION;
      vx *= BOUNCE_FRICTION;
      vz *= BOUNCE_FRICTION;
    }
    stateA[o] = x;
    stateA[o + 1] = y;
    stateA[o + 2] = z;
    stateB[o] = vx;
    stateB[o + 1] = vy;
    stateB[o + 2] = vz;
    stateB[o + 3] = age;
  }
}

/** Particles whose life has not run out at `now`. */
export function aliveCount(b: ParticleBuffers, now: number): number {
  let alive = 0;
  for (let i = 0; i < b.count; i++) {
    const o = i * 4;
    const born = b.emitA[o + 3]!;
    if (born > 0 && now - born < b.emitB[o + 3]!) alive++;
  }
  return alive;
}
